"use client";

import { useContext, useEffect } from "react";
import { AppContext } from "@/context/AppContext";
import { privacyData } from "@/data/modules/privacy";
import { motion } from "framer-motion";

export default function PrivacyPageClient() {
  const { setActivePage } = useContext(AppContext);

  useEffect(() => {
    setActivePage("privacy");
  }, [setActivePage]);

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 12 }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 120, damping: 14, delay: 0.1 }
    }
  };

  const decorationVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: { duration: 0.8, ease: [0.6, 0.05, 0.01, 0.9], delay: 0.2 }
    }
  };

  // Hilfsfunktion zur Formatierung des Inhalts
  const formatContent = (text: string, index: number) => {
    // Überprüfen, ob der Text eine Adresse sein könnte
    const isAddressBlock = /\d{5}/.test(text) && (text.includes("straße") || text.includes("weg") || text.includes("allee") || text.includes("Str.") || text.includes("Hauptstr."));

    if (isAddressBlock) {
      // Formatiere Text als Adresse mit Zeilenumbrüchen statt Absätzen
      return (
        <motion.div
          className="address-block"
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.03 + 0.5 }}
        >
          {text.split(/\n|\r\n|\r/).map((line, lineIdx) => (
            <div key={lineIdx} className="address-line">{line}</div>
          ))}
        </motion.div>
      );
    } else {
      // Normaler Textabsatz
      return (
        <motion.p
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: index * 0.05 + 0.5
          }}
        >
          {text}
        </motion.p>
      );
    }
  };

  return (
    <div className={"py-4 md:py-16 transition-all duration-400"}>

      <div className="content-tile backdrop-blur-sm rounded-xl p-6 md:p-8 bg-[rgba(90,0,0,0.2)] backdrop-blur-sm">
        <motion.h1
          className="page-title"
          variants={titleVariants}
        >
          {privacyData.title}
        </motion.h1>

        <motion.div
          className="title-decoration"
          variants={decorationVariants}
          style={{
            height: "3px",
            width: "100px",
            marginBottom: "2rem",
            transformOrigin: "left",
            borderRadius: "2px"
          }}
        />

        {privacyData.content.map((section, index) => (
          <motion.section
            key={index}
            className="content-section"
            variants={itemVariants}
          >
            <motion.div
              className="section-header"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <h2>{section.title}</h2>
              <div className="section-decoration" style={{
                height: "2px",
                width: "60px",
                marginTop: "0.5rem",
                marginBottom: "1rem",
                borderRadius: "2px"
              }} />
            </motion.div>

            <motion.div
              className="content-text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.1 + 0.4 }}
            >
              {section.text.split("\n\n").map((text, textIndex) => (
                formatContent(text, textIndex)
              ))}
              {section.content && section.content.map((text, textIndex) => (
                formatContent(text, textIndex)
              ))}
            </motion.div>
          </motion.section>
        ))}
      </div>
    </div>
  );
} 